import {
  GridColumnMenuProps,
  GridRowId,
  GridRowSelectionModel,
  GridSortDirection,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { AxiosResponse } from "axios";
import { Dayjs } from "dayjs";
import { IJobRegistrationResponse } from "../../../../api/jobsService/IJobsService";
import { BlobResponse } from "../../../../utils/downloadFile";
import { PendingPromotionCompilationJob } from "../../../../utils/promotions/promotionUtils";

export type Filter = {
  invoiceNumber?: string | null;
  invoiceType: string;
  invoiceTypeList: { key: string; value: string }[];
  srcProcessType?: string | null;
  nfeNumber?: string | null;
  documentNumber?: string | null;
  handlingUnitNumber?: string | null;
  dealerReference: string | null;
  officialDocumentNumber?: string | null;
  partNumber?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
  dealerId?: string | null;
  factura?: string | null;
};

export type FilterConfig = {
  minDate: Dayjs;
};

export enum InvoiceTypeEnum {
  ALL = "all",
  OrderInvoice = "OrderInvoice",
  CreditMemo = "CreditMemo",
  DebitMemo = "DebitMemo",
  D2DOrderInvoice = "D2DOrderInvoice",
  CancelledOrderInvoice = "CancelledOrderInvoice",
  DailyReturnCredit = "DailyReturnCredit",
  SurplusReturnCredit = "SurplusReturnCredit",
}

export enum SourceProcessTypeEnum {
  EmergencyOrder = "Emergency Order",
  StockOrder = "Stock Order",
  PromotionOrder = "Promotion Order",
  DailyReturns = "Daily Returns",
  CoreReturns = "Core Returns",
  Claims = "Claims",
  Order = "Order",
  SurplusReturns = "Surplus Returns",
}

export type FilterWithInfo = Filter & FilterConfig;

export enum FilterType {
  InvoiceNumber = "invoiceNumber",
  InvoiceType = "invoiceType",
  SourceProcessType = "srcProcessType",
  NotaFiscalNumber = "nfeNumber",
  DocumentNumber = "documentNumber",
  HandlingUnitNumber = "handlingUnitNumber",
  DealerReference = "dealerReference",
  OfficialDocumentNumber = "officialDocumentNumber",
  PartNumber = "partNumber",
  DealerAccount = "dealerIdFilter",
  Factura = "factura",
}

export interface InvoicesGridFilterProps {
  filters: FilterWithInfo;
  disableFilters: boolean;
  setFilterValue: (value: string, type: FilterType) => void;
  clearFilterValue: (type: string) => void;
  resetFilters: () => void;
  filterDateMatchDefaultDates: boolean;
  areInvoiceFilterQueryParametersSameAsFilter: boolean;
  onDateRangeChange: (startDate: Dayjs, endDate: Dayjs) => void;
  onSortModelChange: (model: GridSortModel) => void;
}

export interface CheckboxMenuProps {
  dropdownHandler: (select: boolean) => void;
  isDateRangeGreaterThan31: boolean;
  areAnyRowsSelected: boolean;
  noNewInvoices: boolean;
  allNewDropdownHandler: () => void;
}

export interface SelectDropDownMenuProps {
  existingProps: GridColumnMenuProps;
  enableSelectAllPages: boolean;
  enableSelectAllNew: boolean;
  enableSelectNone: boolean;
  selectAllPageTooltip: string;
  selectAllNewTooltip: string;
  onSelectAllPages: () => void;
  onSelectAllNew: () => void;
  onSelectNone: () => void;
}

export type SelectDropDownMenuOptions = Omit<
  SelectDropDownMenuProps,
  "existingProps"
>;

export interface InvoicesFilterState {
  filters: FilterWithInfo;
  pageNumber: number;
  pageSize: number;
  sortField?: string;
  sortDir?: GridSortDirection;
}

export interface InvoicesColumnVisibilityProps {
  showNFENumber: boolean;
  showFacturaNumber: boolean;
  showOfficialDocumentNumber: boolean;
  showViewedDate: boolean;
  showViewedBy: boolean;
  showActions: boolean;
  showDealerAccount: boolean;
  enableInvoiceConsolidationForCheckBox: boolean;
}

export interface InvoicesGridContentVisibilityProps {
  showHULink: boolean;
  hasAccessToViewClaim: boolean;
  downloadingNFeNumber: string;
}

export interface InvoicesGridCallbacks {
  reloadCallback: () => void;
  handleNFEClick: (invoiceNumber: string, nfeNumber: string) => void;
  setUnauthorizedError: (value: boolean) => void;
  selectionChanges: {
    onHeaderSelectChange: (value: boolean) => void;
    onRowSelectChange: (value: boolean, rowId: GridRowId) => void;
  };
}

export interface CheckBoxSelectionProp {
  handleCheckboxSelection: (rowSelectionModel: GridRowSelectionModel) => void;
  rowSelectionModel: GridRowSelectionModel;
}

export interface InvoicesToolbarProps {
  showDownloadButton: boolean;
  showConsolidateInvoiceButton: boolean;
  disableConsolidatedInvoiceDownloadButton: boolean;
  isConsolidateDownloadInProgress: boolean;
  isDownloadingAllInProgress: boolean;
  downloadInvoicesCallback: (
    accessToken: string,
    lang?: string,
    objTypeCode?: string,
    fileName?: string
  ) => Promise<
    AxiosResponse<
      BlobResponse | PendingPromotionCompilationJob | IJobRegistrationResponse
    >
  >;
  consolidateInvoicesCallback: () => Promise<
    AxiosResponse<
      BlobResponse | PendingPromotionCompilationJob | IJobRegistrationResponse
    >
  >;
  showConsolidateInvoicesToExcelButton?: boolean;
  disableConsolidateInvoicesToExcelButton?: boolean;
  consolidateInvoicesToExcelCallback?: (
    accessToken: string
  ) => Promise<AxiosResponse<BlobResponse>>;
  onConsolidateInvoicesToExcelSuccess?: () => void;
}

export interface ToggleProps {
  showViewedDetail: boolean;
}

export interface InvoicesRows {
  id: string;
  invoiceNumber: string;
  invoiceType?: string;
  orderType?: string;
  nfeNumber?: string;
  factura?: string;
  officialDocumentNumber?: string;
  documentNumber: string;
  dealerReference: string;
  createdDate?: string;
  viewedDate?: string;
  viewedBy?: string;
  group: string[];
  isSeen: boolean;
  dealerAccount?: string;
  mruSalesOrg?: string;
  isVendorDirect: boolean;
  isReversed: boolean;
  showNfeLink: boolean;
  handlingUnit?: string;
  showActions: boolean;
}

export interface SelectedInvoices {
  invoiceNumber: string;
  rowId: string;
  accountId?: string;
}

export type InvoicesGridActionMenuProps = {
  onActionOpen: () => void;
  onActionClose: () => void;
  actions: {
    returns: {
      show: boolean;
      enabled: boolean;
      onCreateReturnClick: () => void;
    };
    claims: {
      show: boolean;
      enabled: boolean;
      onCreateClaimsClick: () => void;
    };
    vendorDirectClaims: {
      show: boolean;
      enabled: boolean;
      onCreateVendorDirectClaimClick: () => void;
    };
  };
  isLoading: boolean;
};

export type InvoicesGridActionMenuOptions = Omit<
  InvoicesGridActionMenuProps,
  "onActionOpen" | "actions"
> & {
  onActionOpen: (
    invoiceNumber: string,
    dealerAccountId?: string,
    mruSalesOrg?: string
  ) => void;
  actions: {
    returns: {
      show: boolean;
      enabled: boolean;
      onCreateReturnClick: (accountId: string) => void;
    };
    claims: {
      show: boolean;
      enabled: boolean;
      onCreateClaimsClick: () => void;
    };
    vendorDirectClaims: {
      show: boolean;
      enabled: boolean;
      onCreateVendorDirectClaimClick: () => void;
    };
  };
};
